import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { FFFeatureFlagsService } from '@bannerflow/feature-flags';
import { Logger } from '@bannerflow/sentinel-logger';
import { BrandService } from '@studio/common/brand';
import { EnvironmentService } from '@studio/common/services/environment.service';
import { catchError, filter, forkJoin, of, switchMap, take, timeout } from 'rxjs';

export const featureFlagResolverFn: ResolveFn<unknown> = activatedRoute => {
    const { creativesetId } = activatedRoute.params;
    const brandService = inject(BrandService);
    const environmentService = inject(EnvironmentService);
    const ffFeatureFlagsService = inject(FFFeatureFlagsService);
    const authService = inject(AuthService);

    if (!environmentService.appEnvironment.featureFlags.enabled) {
        return of(undefined);
    }

    const setUserEmail$ = authService.user$.pipe(
        filter(user => !!user),
        take(1)
    );
    const setBrandAndAccount$ = brandService.brand$.pipe(take(1));

    const update$ = ffFeatureFlagsService.update$.pipe(
        take(1),
        timeout(3000),
        catchError(error => {
            const logMessage =
                error instanceof Error && error.name === 'TimeoutError'
                    ? 'Feature Flag Service took to long to be ready.'
                    : error;

            Logger.warn(logMessage);
            return of(undefined);
        })
    );

    return forkJoin([setBrandAndAccount$, setUserEmail$]).pipe(
        switchMap(([brand, user]) => {
            const newContextProperties: Record<string, string> = {
                accountId: brand.accountId,
                accountSlug: brand.accountSlug,
                brandId: brand.id,
                brandSlug: brand.slug,
                creativeSetId: creativesetId
            };

            if (user.email) {
                newContextProperties['userEmail'] = user.email;
            }
            if (user['employee'] === '3') {
                newContextProperties['isEmployee'] = 'true';
            }
            const newContext = {
                userId: user.userId,
                properties: newContextProperties
            };

            ffFeatureFlagsService.updateContext(newContext);
            ffFeatureFlagsService.start();

            return update$;
        })
    );
};
